import React, { useState, useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import AssetPositions from "./AssetPositions";
import PortfolioFilters from "./PortfolioFilters";
import { usePortfolioTableState } from "../../hooks/useOptimizedState";
import { usePortfolioCalculations } from "./usePortfolioCalculations";
import { usePortfolioFilters } from "./usePortfolioFilters";
import { usePortfolioUpdates } from "./usePortfolioUpdates";
import { processData } from "../../component/Methods";

const PortfolioTable = ({ fundname, strategyname, setTotals, threshold, onExportDataChange }) => {
  const [filteredValues, setFilteredValues] = useState([]);

  // Get state from optimized hook
  const {
    funds,
    lots,
    accountingData,
    signalsPerformance,
    markets,
    livePrices,
    isStrategist,
    managers,
    strategies,
    trades,
  } = usePortfolioTableState();

  // Use custom hooks for different concerns
  const { fund, calculatePositions } = usePortfolioCalculations({
    funds,
    lots,
    accountingData,
    strategies,
    livePrices,
    trades,
    markets,
    isStrategist,
    managers,
    signalsPerformance,
  });

  const {
    fundVal,
    StrategeyVal,
    StrategeyTypename,
    fundOptions,
    strategyOptions,
    StrategyType,
    disableStrategyButton,
    StrategyName,
    setFundFilter,
    setStrategyVal,
    setStrategyTypename,
    setStrategyType,
  } = usePortfolioFilters({
    funds,
    strategies,
    isStrategist,
    fundname,
    strategyname,
    fund,
  });

  // Handle debounced updates
  usePortfolioUpdates({
    calculatePositions,
    fundVal,
    StrategeyVal,
    StrategeyTypename,
    setFilteredValues,
    setTotals,
  });

  // Process data when strategy changes
  useEffect(() => {
    let matchingStrategies;

    if (
      StrategeyVal !== undefined &&
      StrategeyVal !== "ALL" &&
      StrategeyVal !== ""
    ) {
      if (StrategeyTypename === "Both") {
        matchingStrategies = strategies?.find(
          (strategy) => strategy.name === StrategeyVal
        );
      } else if (
        StrategeyTypename === "Long" ||
        StrategeyTypename === "Short"
      ) {
        matchingStrategies = strategies?.find((strategy) =>
          strategy.name.includes(`${StrategeyVal}_${StrategeyTypename}`)
        );
      }
    }

    if (matchingStrategies && matchingStrategies.strategyId) {
      processData(strategies, matchingStrategies.strategyId);
    }
  }, [StrategeyVal, StrategeyTypename, strategies]);

  // Memoized event handlers
  const handleFundChange = useCallback(
    (e, newValue) => {
      setFundFilter(newValue);
      calculatePositions(
        newValue,
        StrategeyVal,
        StrategeyTypename,
        setFilteredValues,
        setTotals
      );
    },
    [
      setFundFilter,
      calculatePositions,
      StrategeyVal,
      StrategeyTypename,
      setFilteredValues,
      setTotals,
    ]
  );

  const handleStrategyChange = useCallback(
    (e, newValue) => {
      calculatePositions(
        fundVal,
        newValue,
        StrategeyTypename,
        setFilteredValues,
        setTotals
      );
      setStrategyTypename("Both");
      if (newValue === "ALL") {
        setStrategyType(["Both"]);
        setStrategyVal("ALL");
      } else {
        setStrategyVal(newValue);
        const selectedStrategy = StrategyName?.find(
          (strategy) => strategy.name === newValue
        );
        if (selectedStrategy && selectedStrategy.checkType === "noLS") {
          setStrategyType(["Both"]);
        } else {
          isStrategist
            ? setStrategyType(["Both"])
            : setStrategyType(["Both", "Long", "Short"]);
        }
      }
    },
    [
      calculatePositions,
      fundVal,
      StrategeyTypename,
      StrategyName,
      isStrategist,
      setStrategyTypename,
      setStrategyType,
      setStrategyVal,
      setFilteredValues,
      setTotals,
    ]
  );

  const handleSideChange = useCallback(
    (e, newValue) => {
      calculatePositions(
        fundVal,
        StrategeyVal,
        newValue,
        setFilteredValues,
        setTotals
      );
      setStrategyTypename(newValue);
    },
    [
      calculatePositions,
      fundVal,
      StrategeyVal,
      setStrategyTypename,
      setFilteredValues,
      setTotals,
    ]
  );

  const handlePositionsExportData = useCallback(
    ({ long, short }) => {
      onExportDataChange?.({
        fundVal,
        StrategeyVal,
        StrategeyTypename,
        longPositions: long,
        shortPositions: short,
      });
    },
    [onExportDataChange, fundVal, StrategeyVal, StrategeyTypename]
  );

  useEffect(() => {
    onExportDataChange?.({
      fundVal,
      StrategeyVal,
      StrategeyTypename,
      longPositions: [],
      shortPositions: [],
    });
  }, [fundVal, StrategeyVal, StrategeyTypename, onExportDataChange]);

  // Initial data calculation
  useEffect(() => {
    if (funds && strategies) {
      calculatePositions(
        fundVal,
        StrategeyVal,
        StrategeyTypename,
        setFilteredValues,
        setTotals
      );
    }
  }, [
    funds,
    strategies,
    calculatePositions,
    fundVal,
    StrategeyVal,
    StrategeyTypename,
    setFilteredValues,
    setTotals,
  ]);

  return (
    <Box sx={{ marginLeft: 3, marginTop: 1 }}>
      <Box>
        <PortfolioFilters
          fundVal={fundVal}
          StrategeyVal={StrategeyVal}
          StrategeyTypename={StrategeyTypename}
          fundOptions={fundOptions}
          strategyOptions={strategyOptions}
          StrategyType={StrategyType}
          isStrategist={isStrategist}
          disableStrategyButton={disableStrategyButton}
          StrategyName={StrategyName}
          handleFundChange={handleFundChange}
          handleStrategyChange={handleStrategyChange}
          handleSideChange={handleSideChange}
        />

        <AssetPositions
          data={filteredValues}
          fundVal={fundVal}
          StrategeyVal={StrategeyVal}
          onExportDataChange={handlePositionsExportData}
        />
      </Box>
    </Box>
  );
};

export default React.memo(PortfolioTable);
